import { useState, FormEvent, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteSettings, useSocialLinks } from "@/hooks/content";
import { supabase } from "@/lib/supabase";
import Reveal from "@/components/base/Reveal";

export default function ContactPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const { record: settings } = useSiteSettings();
  const { record: social } = useSocialLinks();

  const departments = [
    t("contact.dept.general"),
    t("contact.dept.newsroom"),
    t("contact.dept.advertising"),
    t("contact.dept.partnerships"),
    t("contact.dept.technical"),
    t("contact.dept.careers"),
    t("contact.dept.programs"),
  ];

  const socialItems = [
    { icon: "ri-facebook-fill", color: "bg-[#1877F2]", url: social.facebook },
    { icon: "ri-youtube-fill", color: "bg-[#FF0000]", url: social.youtube },
    { icon: "ri-tiktok-fill", color: "bg-foreground-900", url: social.tiktok },
    { icon: "ri-instagram-fill", color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]", url: social.instagram },
    { icon: "ri-twitter-x-fill", color: "bg-foreground-900", url: social.twitter },
    { icon: "ri-whatsapp-fill", color: "bg-[#25D366]", url: social.whatsapp },
  ].filter((s) => s.url);

  useEffect(() => {
    document.title = "Contact | L+TV TÉLÉVISION";
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = ((formData.get("website_alt") as string) || "").trim();

    if (honeypot) {
      setError("");
      setSubmitted(true);
      form.reset();
      return;
    }

    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const subject = formData.get("subject") as string;
    const department = formData.get("department") as string;
    const message = formData.get("message") as string;

    if (!name?.trim() || !email?.trim() || !subject?.trim() || !message?.trim()) {
      setError(t("contact.required"));
      return;
    }
    if (!email.includes("@")) {
      setError(t("contact.invalidEmail"));
      return;
    }

    setError("");
    try {
      const { error: insertError } = await supabase.from("contact_messages").insert({
        id: crypto.randomUUID(),
        name: name.trim(),
        email: email.trim(),
        phone: phone?.trim() || null,
        subject: subject.trim(),
        department: department?.trim() || null,
        message: message.trim(),
        read: false,
        created_at: new Date().toISOString(),
      });
      if (insertError) throw insertError;

      if (settings.email) {
        supabase.functions.invoke("send-email", {
          body: {
            to: settings.email,
            subject: `New contact message: ${subject.trim()}`,
            html: `<div style="font-family:Arial,sans-serif;max-width:520px;margin:0 auto;padding:24px;color:#1f2937;"><h1 style="font-size:18px;margin:0 0 12px;">New contact message</h1><p style="font-size:14px;color:#6b7280;"><strong>Name:</strong> ${name.trim()}<br/><strong>Email:</strong> ${email.trim()}<br/><strong>Department:</strong> ${department || "—"}<br/><strong>Subject:</strong> ${subject.trim()}</p><p style="font-size:14px;color:#1f2937;margin-top:12px;">${message.trim()}</p></div>`,
          },
        }).catch(() => {});
      }

      setSubmitted(true);
      form.reset();
    } catch {
      setError(t("contact.error"));
    }
  };

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <Reveal direction="up">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t("contact.title")}</h1>
            <p className="text-white/60 text-sm mt-1">{t("contact.subtitle")}</p>
          </Reveal>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <Reveal direction="right">
              <div className="bg-white rounded-lg border border-background-200 p-5">
                <h3 className="font-semibold text-foreground-900 mb-4">{t("contact.info")}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 flex-shrink-0">
                      <i className="ri-building-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{settings.siteName || "L+TV TÉLÉVISION"}</p>
                      <p className="text-sm text-foreground-600">{settings.poBox ? `P.O. Box: ${settings.poBox}` : "P.O. Box: EDEA - Cameroon"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("contact.email")}</p>
                      <p className="text-sm text-foreground-600">{settings.email || "contact@ltvtelevision.com"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t("contact.phone")}</p>
                      <p className="text-sm text-foreground-600">{settings.phone || "+237 (Placeholder)"}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-background-200 p-5">
                <h3 className="font-semibold text-foreground-900 mb-4">{t("contact.follow")}</h3>
                <div className="flex flex-wrap gap-2">
                  {socialItems.map((s, i) => (
                    <a key={i} href={s.url} className={`w-9 h-9 flex items-center justify-center rounded-md ${s.color} text-white hover:opacity-90 transition-opacity`}>
                      <i className={`${s.icon} text-sm`}></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg border border-background-200 p-5">
                <h3 className="font-semibold text-foreground-900 mb-4">{t("contact.location")}</h3>
                <div className="rounded-lg overflow-hidden bg-background-100 aspect-video flex items-center justify-center">
                  <div className="text-center p-4">
                    <i className="ri-map-pin-2-line text-3xl text-foreground-300 mb-2"></i>
                    <p className="text-sm text-foreground-500">{t("contact.edea")}</p>
                    <p className="text-xs text-foreground-400 mt-1">{t("contact.mapNote")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Reveal direction="left">
              <div className="bg-white rounded-lg border border-background-200 p-6 md:p-8">
                <h2 className="text-lg font-bold text-foreground-900 mb-2">{t("contact.sendTitle")}</h2>
                <p className="text-sm text-foreground-600 mb-6">{t("contact.sendDesc")}</p>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white mx-auto mb-3">
                      <i className="ri-check-line text-xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-green-800 mb-1">{t("contact.messageSent")}</h3>
                    <p className="text-sm text-green-700">{t("contact.messageSentDesc")}</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.fullName")} *</label>
                        <input name="name" type="text" className="ltv-input" placeholder={t("contact.yourName")} required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.emailAddress")} *</label>
                        <input name="email" type="email" className="ltv-input" placeholder={t("contact.yourEmail")} required />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.phoneNumber")}</label>
                        <input name="phone" type="tel" className="ltv-input" placeholder="+237 ..." />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.subject")} *</label>
                        <input name="subject" type="text" className="ltv-input" placeholder={t("contact.subjectPlaceholder")} required />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.department")}</label>
                      <select name="department" className="ltv-input bg-white">
                        {departments.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1">{t("contact.message")} *</label>
                      <textarea name="message" rows={5} className="ltv-input resize-none" placeholder={t("contact.messagePlaceholder")} maxLength={500} required></textarea>
                      <p className="text-xs text-foreground-400 mt-1">{t("contact.maxChars")}</p>
                    </div>

                    {/* Honeypot */}
                    <div className="hidden-field" style={{ position: "absolute", left: "-9999px" }}>
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    {error && <p className="text-sm text-accent-600">{error}</p>}

                    <button type="submit" className="ltv-btn-accent w-full sm:w-auto">
                      {t("contact.send")}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}